/**
 * SALES-FORECASTING MODULE
 * Self-contained. Import only from /shared or within this module.
 * Do not import from other modules directly.
 */

// forecastAggregate.js — the single source of truth for how forecast rows are
// filtered, grouped (by Bottle Size / Pack Size), and totalled.
//
// Both ForecastGrid (on-screen) and exportForecast (spreadsheet) call
// buildForecastView() so the exported numbers are guaranteed to match exactly
// what's on the screen. It supports one or many selected years: every SKU row
// carries a per-year × per-month cell grid, with per-year and all-years totals,
// plus group (size/pack) subtotals and grand totals.

export const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export const MONTH_ABBR = MONTHS.map((m) => m.slice(0, 3));

/** Label for a Bottle Size / Pack Size group, e.g. "1.75L / 6pk". */
export function skuGroupLabel(size, packSize) {
  const s = (size || "").trim();
  const p = (packSize || "").trim();
  if (!s && !p) return "Other";
  return `${s || "—"} / ${p || "—"}`;
}

/**
 * Build the grouped, multi-year forecast view.
 *
 * @param {Object}   opts
 * @param {Array}    opts.rawForecast          rows with a `fields` envelope (see forecastService)
 * @param {string[]} opts.years                selected years (any order; normalized to sorted strings)
 * @param {string}   [opts.selectedRegion]
 * @param {string}   [opts.selectedState]
 * @param {string}   [opts.selectedDistributor]
 * @param {string}   [opts.selectedSku]        if set, restrict to this one SKU
 * @param {string[]} opts.skuNames             full ordered SKU list (drives row order + membership)
 * @param {Object}   [opts.skuToSize]          { [skuName]: bottleSizeName }
 * @param {Object}   [opts.skuToPackSize]      { [skuName]: packSizeName }
 *
 * @returns {{
 *   years: string[],
 *   groups: Array<{
 *     label: string, size: string, packSize: string,
 *     rows: Array<{
 *       sku: string,
 *       cells: Object,        // { [year]: { [month]: { value, id, modifiedBy, modified } | null } }
 *       yearTotals: Object,   // { [year]: number }
 *       total: number,        // all-years total for the SKU
 *     }>,
 *     groupYM: Object,        // { [year]: { [month]: number } }  group subtotal per cell
 *     groupYearTotals: Object,// { [year]: number }
 *     groupTotal: number,
 *   }>,
 *   colYM: Object,            // { [year]: { [month]: number } }  grand column total per cell
 *   colYearTotals: Object,    // { [year]: number }
 *   grandTotal: number,
 *   yearHasData: Object,      // { [year]: boolean }  — see below (backlog 214)
 * }}
 */
export function buildForecastView({
  rawForecast = [],
  years = [],
  selectedRegion = "",
  selectedState = "",
  selectedDistributor = "",
  selectedSku = "",
  skuNames = [],
  skuToSize = {},
  skuToPackSize = {},
}) {
  const yrs = [...new Set(years.map(String))].sort();
  const ySet = new Set(yrs);

  const visibleSkus = selectedSku ? skuNames.filter((s) => s === selectedSku) : skuNames;

  // cellMap: sku -> year -> month -> { value, id, modifiedBy, modified }
  // id is kept only when a single record backs the cell (single-state mode) so
  // the grid can edit/save it; when multiple records aggregate, id is nulled.
  const cellMap = {};
  if (yrs.length) {
    rawForecast
      .filter((r) => {
        if (!ySet.has(String(r.fields.Year))) return false;
        if (selectedRegion && r.fields.Region !== selectedRegion) return false;
        if (selectedState && r.fields.State !== selectedState) return false;
        if (selectedDistributor && r.fields.Distributor !== selectedDistributor) return false;
        return true;
      })
      .forEach((r) => {
        const sku = r.fields.ProductSKU;
        const yr = String(r.fields.Year);
        const month = r.fields.Month;
        if (!sku || !month || !ySet.has(yr)) return;
        cellMap[sku] ??= {};
        cellMap[sku][yr] ??= {};
        const cur = cellMap[sku][yr][month];
        if (!cur) {
          cellMap[sku][yr][month] = {
            value: r.fields.CaseForecast ?? 0,
            id: r.id,
            modifiedBy: r.fields.ModifiedByDisplay || "",
            modified: r.fields.Modified || "",
          };
        } else {
          cur.id = null; // multiple records → not directly editable
          cur.value = (cur.value ?? 0) + (r.fields.CaseForecast ?? 0);
          cur.modifiedBy = "";
          cur.modified = "";
        }
      });
  }

  // Group SKUs by Bottle Size / Pack Size, preserving skuNames order within a group.
  const groupsMap = new Map();
  visibleSkus.forEach((sku) => {
    const size = skuToSize[sku] || "";
    const packSize = skuToPackSize[sku] || "";
    const label = skuGroupLabel(size, packSize);
    if (!groupsMap.has(label)) groupsMap.set(label, { label, size, packSize, skus: [] });
    groupsMap.get(label).skus.push(sku);
  });
  const groupLabels = [...groupsMap.keys()].sort((a, b) => a.localeCompare(b));

  const colYM = {};
  const colYearTotals = {};
  yrs.forEach((y) => { colYM[y] = {}; colYearTotals[y] = 0; });
  let grandTotal = 0;

  // backlog 214 — totals cannot answer "is there a forecast for this year?":
  // a year with no rows at all and a year forecast to zero both total 0. Track
  // record EXISTENCE per year, against the rows the caller will actually render
  // (visibleSkus + the region/state/distributor filters), so the grid can say
  // "no forecast for 2027" instead of drawing a convincing wall of em-dashes.
  const yearHasData = {};
  yrs.forEach((y) => { yearHasData[y] = false; });

  const groups = groupLabels.map((label) => {
    const g = groupsMap.get(label);
    const groupYM = {};
    const groupYearTotals = {};
    yrs.forEach((y) => { groupYM[y] = {}; groupYearTotals[y] = 0; });
    let groupTotal = 0;

    const rows = g.skus.map((sku) => {
      const cells = {};
      const yearTotals = {};
      let total = 0;
      yrs.forEach((y) => {
        cells[y] = {};
        yearTotals[y] = 0;
        MONTHS.forEach((m) => {
          const cell = cellMap[sku]?.[y]?.[m] || null;
          cells[y][m] = cell;
          if (cell) yearHasData[y] = true;   // backlog 214
          const v = cell?.value ?? 0;
          yearTotals[y] += v;
          total += v;
          groupYM[y][m] = (groupYM[y][m] ?? 0) + v;
          groupYearTotals[y] += v;
          groupTotal += v;
          colYM[y][m] = (colYM[y][m] ?? 0) + v;
          colYearTotals[y] += v;
          grandTotal += v;
        });
      });
      return { sku, cells, yearTotals, total };
    });

    return { label, size: g.size, packSize: g.packSize, rows, groupYM, groupYearTotals, groupTotal };
  });

  return { years: yrs, groups, colYM, colYearTotals, grandTotal, yearHasData };
}

/**
 * Build chart series split by a chosen dimension — the export's chart tab uses
 * this (it mirrors ForecastChart's on-screen grouping). Single year → Jan…Dec;
 * multiple years → a continuous "Jan '25 … Dec '26" timeline.
 *
 * @returns {{ labels: string[], series: Array<{ name, total, values: number[] }>, multiYear: boolean }}
 */
export function buildChartSeries({
  rawForecast = [],
  years = [],
  selectedRegion = "",
  selectedState = "",
  selectedDistributor = "",
  selectedSku = "",
  groupBy = "sku",
  skuToSize = {},
  skuToPackSize = {},
  skuToFlavor = {},
  skuToTag = {},
}) {
  const yrs = [...new Set(years.map(String))].sort();
  if (!yrs.length) return { labels: [], series: [], multiYear: false };
  const ySet = new Set(yrs);
  const multiYear = yrs.length > 1;
  const skuFilter = selectedSku ? new Set([selectedSku]) : null;

  const slots = [];
  yrs.forEach((y) => MONTHS.forEach((m, i) => slots.push({
    year: y, month: m,
    label: multiYear ? `${MONTH_ABBR[i]} '${y.slice(2)}` : MONTH_ABBR[i],
  })));
  const slotIndex = {};
  slots.forEach((s, i) => { slotIndex[`${s.year}|${s.month}`] = i; });

  const labelsFor = (r) => {
    const sku = r.fields.ProductSKU;
    switch (groupBy) {
      case "region":      return [r.fields.Region || "Unspecified"];
      case "state":       return [r.fields.State || "Unspecified"];
      case "distributor": return [r.fields.Distributor || "Unspecified"];
      case "size":        return [skuToSize[sku] || "Unspecified"];
      case "packSize":    return [skuToPackSize[sku] || "Unspecified"];
      case "flavor":      return [skuToFlavor[sku] || "Unspecified"];
      case "tag": {
        const t = skuToTag[sku];
        return Array.isArray(t) && t.length ? t : ["Untagged"];
      }
      case "sku":
      default:            return [sku || "Unspecified"];
    }
  };

  const acc = slots.map(() => ({}));
  const totals = {};
  rawForecast
    .filter((r) => {
      if (!ySet.has(String(r.fields.Year))) return false;
      if (selectedRegion && r.fields.Region !== selectedRegion) return false;
      if (selectedState && r.fields.State !== selectedState) return false;
      if (selectedDistributor && r.fields.Distributor !== selectedDistributor) return false;
      return true;
    })
    .forEach((r) => {
      const sku = r.fields.ProductSKU;
      const month = r.fields.Month;
      const yr = String(r.fields.Year);
      if (!sku || !month) return;
      if (skuFilter && !skuFilter.has(sku)) return;
      const idx = slotIndex[`${yr}|${month}`];
      if (idx == null) return;
      const v = r.fields.CaseForecast ?? 0;
      labelsFor(r).forEach((name) => {
        acc[idx][name] = (acc[idx][name] ?? 0) + v;
        totals[name] = (totals[name] ?? 0) + v;
      });
    });

  const names = Object.keys(totals)
    .filter((n) => totals[n] > 0)
    .sort((a, b) => totals[b] - totals[a] || a.localeCompare(b));

  return {
    labels: slots.map((s) => s.label),
    series: names.map((n) => ({ name: n, total: totals[n], values: slots.map((_, i) => acc[i][n] ?? 0) })),
    multiYear,
  };
}
